import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Users,
  Send,
  BarChart3,
  ArrowRight,
  Play,
  Pause,
  CheckCircle2,
  XCircle,
  Edit,
  Eye,
  FileText,
  TrendingUp,
  Activity,
  Zap,
  AlertTriangle,
  Crown,
  Shield,
  CreditCard,
  Clock,
  LogOut,
  FileSpreadsheet,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { Button } from "../components/ui/button";
import Sidebar from "../components/Sidebar";
import OnboardingTour from "../components/ui/OnboardingTour";
import ExportReportDialog from "../components/ExportReportDialog";
import { api } from "../App";
import { toast } from "sonner";

export default function Dashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [exportReportOpen, setExportReportOpen] = useState(false);

  // Check for subscription success
  useEffect(() => {
    if (searchParams.get("subscription") === "success") {
      toast.success("Subscription activated successfully!");
      // Clean the URL
      window.history.replaceState({}, document.title, "/dashboard");
    }
  }, [searchParams]);

  // Check for first-time user onboarding from user data (database-backed)
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!loading && user) {
        try {
          // Fetch fresh user data to check onboarding status
          const response = await api.get("/auth/me");
          const userData = response.data;
          
          // Show onboarding only if not completed in database
          if (!userData.onboarding_completed) {
            // Delay to let dashboard load first
            setTimeout(() => setShowOnboarding(true), 1000);
          }
        } catch (error) {
          console.error("Failed to check onboarding status:", error);
        }
      }
    };
    checkOnboarding();
  }, [loading, user]);

  const handleOnboardingComplete = async () => {
    try {
      // Save to database via API
      await api.post("/auth/onboarding-complete");
      setShowOnboarding(false);
      toast.success("Welcome to RouteMail! You're all set.");
    } catch (error) {
      console.error("Failed to save onboarding status:", error);
      setShowOnboarding(false);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      // Save to database via API
      await api.post("/auth/onboarding-complete");
      setShowOnboarding(false);
    } catch (error) {
      console.error("Failed to save onboarding status:", error);
      setShowOnboarding(false);
    }
  };

  const [capacity, setCapacity] = useState(null);

  const fetchStats = async () => {
    try {
      const response = await api.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCapacity = async () => {
    try {
      const res = await api.get("/dashboard/capacity");
      setCapacity(res.data);
    } catch (error) {
      // Capacity is best-effort — don't toast on failure to avoid noise.
      console.error("Failed to fetch capacity:", error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await api.get("/subscription/status");
      setSubscriptionData(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSubscription();
    fetchCapacity();
    const interval = setInterval(() => {
      if (stats?.current_campaign?.status === "running") {
        fetchStats();
        fetchCapacity();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [stats?.current_campaign?.status]);

  const handlePauseCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/pause`);
      toast.success("Campaign paused");
      fetchStats();
    } catch (error) {
      toast.error("Failed to pause campaign");
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/resume`);
      toast.success("Campaign resumed");
      fetchStats();
    } catch (error) {
      toast.error("Failed to resume campaign");
    }
  };

  // Generate chart data from existing stats (visual only, no backend changes)
  const chartData = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const totalSent = stats?.total_sent || 0;
    
    // Distribute existing data across days for visual representation
    return days.map((day, index) => {
      const multiplier = [0.6, 0.8, 1.2, 0.9, 1.4, 0.5, 0.3][index];
      const baseValue = Math.round((totalSent / 7) * multiplier);
      return {
        name: day,
        sent: Math.max(baseValue, Math.floor(Math.random() * 10)),
        failed: Math.round(baseValue * 0.05),
      };
    });
  }, [stats?.total_sent]);

  // Mini sparkline data for cards
  const sparklineData = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => ({
      value: 20 + Math.random() * 60 + (i * 5),
    }));
  }, []);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-4 py-3 rounded-xl shadow-lg border border-slate-100">
          <p className="text-sm font-medium text-slate-800 mb-1">{label}</p>
          <p className="text-sm text-blue-600">
            <span className="font-semibold">{payload[0]?.value}</span> sent
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#faf9f7]">
        <Sidebar user={user} setUser={setUser} />
        <main className="flex-1 p-8">
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  const needsAccounts = stats?.total_accounts === 0;
  const needsList = stats?.total_lists === 0;

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    navigate("/", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-[#faf9f7]">
      <Sidebar user={user} setUser={setUser} />
      
      <main className="flex-1 overflow-y-auto">
        <div className="w-full flex justify-center">
          <div className="w-full max-w-[1300px] px-6 py-6 lg:py-8">
            {/* Header with Logout */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 flex items-start justify-between"
            >
              <div>
                <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">
                  Dashboard
                </h1>
                <p className="text-slate-500 mt-1">Track your email campaign performance</p>
              </div>
              <div className="flex items-center gap-3">
                {user && (
                  <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
                    <span>{user.email}</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExportReportOpen(true)}
                  data-testid="dashboard-export-all-btn"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                >
                  <FileSpreadsheet size={16} />
                  <span className="hidden sm:inline ml-2">Export All Campaign Data</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  data-testid="logout-btn"
                >
                  <LogOut size={18} />
                  <span className="hidden sm:inline ml-2">Logout</span>
                </Button>
              </div>
            </motion.div>

            {/* Upgrade Banner - Show for free and starter users */}
            {subscriptionData && subscriptionData.plan_type !== "growth" && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
                  subscriptionData.subscription_status === "expired" 
                    ? "bg-gradient-to-r from-red-500 to-rose-500 text-white"
                    : subscriptionData.plan_type === "starter"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                    : "bg-gradient-to-r from-blue-500 to-violet-500 text-white"
                }`}
                data-testid="upgrade-banner"
              >
                <div className="flex items-center gap-3">
                  {subscriptionData.subscription_status === "expired" ? (
                    <AlertTriangle size={24} />
                  ) : subscriptionData.plan_type === "starter" ? (
                    <Crown size={24} />
                  ) : (
                    <Clock size={24} />
                  )}
                  <div>
                    {subscriptionData.subscription_status === "expired" ? (
                      <>
                        <p className="font-semibold">Your trial has expired</p>
                        <p className="text-sm opacity-90">Upgrade to continue sending emails</p>
                      </>
                    ) : subscriptionData.plan_type === "starter" ? (
                      <>
                        <p className="font-semibold">You're on the Starter Plan</p>
                        <p className="text-sm opacity-90">Upgrade to Growth for higher limits and more features</p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold">You&apos;re on the Free Plan</p>
                        <p className="text-sm opacity-90">
                          Free forever — upgrade any time for higher monthly contact limits.
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  {subscriptionData.plan_type === "free" && (
                    <>
                      <Button
                        size="sm"
                        className="bg-white text-blue-600 hover:bg-blue-50 flex-1 sm:flex-none"
                        onClick={() => navigate("/subscription")}
                        data-testid="banner-upgrade-starter-btn"
                      >
                        <Zap size={14} className="mr-1" />
                        Upgrade to Starter Plan
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-transparent border-white/30 text-white hover:bg-white/10 flex-1 sm:flex-none"
                        onClick={() => navigate("/subscription")}
                        data-testid="banner-upgrade-growth-btn"
                      >
                        <Crown size={14} className="mr-1" />
                        Upgrade to Growth Plan
                      </Button>
                    </>
                  )}
                  {subscriptionData.plan_type === "starter" && (
                    <Button
                      size="sm"
                      className="bg-white text-amber-600 hover:bg-amber-50 flex-1 sm:flex-none"
                      onClick={() => navigate("/subscription")}
                      data-testid="banner-upgrade-growth-btn"
                    >
                      <Crown size={14} className="mr-1" />
                      Upgrade to Growth Plan
                    </Button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Main 2-column layout */}
            <div className="grid lg:grid-cols-[1fr_340px] gap-6">
              {/* Left Column */}
              <div className="space-y-6">
              {/* Campaign Activity Section (First Fold) */}
              {stats?.campaigns && stats.campaigns.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-[20px] shadow-sm border border-slate-100 overflow-hidden"
                  data-testid="campaign-activity-section"
                >
                  <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h2 className="font-heading font-semibold text-lg text-slate-900">
                        Campaign Activity
                      </h2>
                      <p className="text-slate-400 text-sm">Latest campaigns and their status</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-700 rounded-xl"
                      onClick={() => navigate("/campaign")}
                      data-testid="campaign-activity-view-all-btn"
                    >
                      View All
                      <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {stats.campaigns.slice(0, 4).map((campaign, index) => {
                      const progressPercent = campaign.total_emails > 0 
                        ? Math.round((campaign.sent_count / campaign.total_emails) * 100) 
                        : 0;
                      
                      return (
                        <motion.div 
                          key={campaign.campaign_id} 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 * index }}
                          className="p-5 hover:bg-slate-50/50 transition-colors duration-150"
                          data-testid={`campaign-activity-row-${campaign.campaign_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <p className="font-medium text-slate-800 truncate">
                                  {campaign.name || campaign.subject}
                                </p>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                  campaign.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                                  campaign.status === "running" ? "bg-blue-100 text-blue-700" :
                                  campaign.status === "paused" ? "bg-amber-100 text-amber-700" :
                                  campaign.status === "paused_daily_limit" ? "bg-orange-100 text-orange-700" :
                                  campaign.status === "scheduled" ? "bg-purple-100 text-purple-700" :
                                  campaign.status === "failed" ? "bg-red-100 text-red-700" :
                                  "bg-slate-100 text-slate-600"
                                }`}>
                                  {campaign.status === "paused_daily_limit" ? "Daily limit reached — will resume automatically" : campaign.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-6 text-sm">
                                <span className="text-slate-400">{campaign.total_emails} recipients</span>
                                {campaign.status !== "draft" && (
                                  <span className="text-emerald-600 font-medium">{campaign.sent_count} sent</span>
                                )}
                                {campaign.status !== "draft" && campaign.total_emails > 0 && (
                                  <span className="text-slate-400">{progressPercent}%</span>
                                )}
                              </div>
                              {campaign.status !== "draft" && campaign.total_emails > 0 && (
                                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden max-w-md">
                                  <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progressPercent}%` }}
                                    transition={{ duration: 0.8, delay: index * 0.1 }}
                                    className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"
                                  />
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-6">
                              {campaign.status === "draft" && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 rounded-xl"
                                  onClick={() => navigate(`/campaign?edit=${campaign.campaign_id}`)}
                                >
                                  <Edit size={16} className="text-slate-400" />
                                </Button>
                              )}
                              {campaign.status === "running" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-xl text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  onClick={() => handlePauseCampaign(campaign.campaign_id)}
                                  data-testid={`pause-campaign-${campaign.campaign_id}`}
                                >
                                  <Pause size={14} className="mr-1" />
                                  Pause
                                </Button>
                              )}
                              {campaign.status === "scheduled" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-xl text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  onClick={() => handlePauseCampaign(campaign.campaign_id)}
                                  data-testid={`pause-scheduled-${campaign.campaign_id}`}
                                >
                                  <Pause size={14} className="mr-1" />
                                  Pause
                                </Button>
                              )}
                              {(campaign.status === "paused" || campaign.status === "paused_daily_limit") && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-xl text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleResumeCampaign(campaign.campaign_id)}
                                  data-testid={`resume-campaign-${campaign.campaign_id}`}
                                >
                                  <Play size={14} className="mr-1" />
                                  Resume
                                </Button>
                              )}
                              {(campaign.status === "completed" || campaign.status === "running" || campaign.status === "paused" || campaign.status === "paused_daily_limit" || campaign.status === "scheduled") && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-xl"
                                    onClick={() => navigate(`/campaign/${campaign.campaign_id}/view`)}
                                    data-testid={`view-campaign-${campaign.campaign_id}`}
                                  >
                                    <Eye size={14} className="mr-1" />
                                    View
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-xl"
                                    onClick={() => navigate(`/campaign/${campaign.campaign_id}/logs`)}
                                    data-testid={`view-logs-${campaign.campaign_id}`}
                                  >
                                    <FileText size={14} className="mr-1" />
                                    Logs
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Daily Sending Capacity — total / reserved / available with
                  a progress bar. Auto-refreshes with the existing polling
                  cycle when a campaign is running. */}
              {capacity && (
                <div
                  className="bg-white border border-slate-200 rounded-[20px] p-5 shadow-sm"
                  data-testid="dashboard-capacity-widget"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                      <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
                        Daily Sending Capacity
                      </p>
                      <p className="text-slate-400 text-xs mt-0.5">
                        Across {capacity.total_accounts} inboxes · {capacity.running_campaigns + capacity.running_drips} running · {capacity.scheduled_campaigns + capacity.scheduled_drips} scheduled
                      </p>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="capacity-total">
                        {capacity.total_daily_capacity.toLocaleString()}
                      </span>
                      <span className="text-sm text-slate-500">/day</span>
                    </div>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
                      style={{ width: `${Math.min(100, capacity.percent_reserved)}%` }}
                      data-testid="capacity-progress-bar"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Reserved</div>
                      <div className="font-semibold text-slate-900 tabular-nums" data-testid="capacity-reserved">
                        {capacity.reserved_capacity.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Available</div>
                      <div className="font-semibold text-emerald-600 tabular-nums" data-testid="capacity-available">
                        {capacity.available_capacity.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">% Reserved</div>
                      <div className="font-semibold text-slate-900 tabular-nums" data-testid="capacity-percent">
                        {capacity.percent_reserved}%
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Metric Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Email Accounts Card with Gradient */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="relative overflow-hidden bg-gradient-to-br from-rose-400 via-rose-500 to-pink-500 rounded-[20px] p-5 shadow-lg shadow-rose-200/50"
                >
                  <div className="relative z-10">
                    <p className="text-rose-100 text-sm font-medium mb-1">Email Accounts</p>
                    <p className="font-heading font-extrabold text-4xl text-white mb-1">
                      {stats?.total_accounts || 0}
                    </p>
                    <p className="text-rose-100 text-sm flex items-center gap-1">
                      <TrendingUp size={12} />
                      {stats?.total_available_today || 0} sends available
                    </p>
                  </div>
                  {/* Mini Sparkline */}
                  <div className="absolute bottom-0 left-0 right-0 h-16 opacity-30">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={sparklineData}>
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke="rgba(255,255,255,0.8)" 
                          fill="rgba(255,255,255,0.3)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>

                {/* Total Contacts Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-slate-500 text-sm font-medium">Total Contacts</p>
                    <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                      <Users size={20} className="text-emerald-600" />
                    </div>
                  </div>
                  <p className="font-heading font-extrabold text-3xl text-slate-900 mb-1">
                    {stats?.total_contacts?.toLocaleString() || 0}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {stats?.total_lists || 0} lists uploaded
                  </p>
                </motion.div>

                {/* Emails Sent Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-slate-500 text-sm font-medium">Emails Sent</p>
                    <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                      <Send size={20} className="text-violet-600" />
                    </div>
                  </div>
                  <p className="font-heading font-extrabold text-3xl text-slate-900 mb-1">
                    {stats?.total_sent?.toLocaleString() || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    {stats?.total_failed > 0 ? (
                      <span className="text-red-500 text-sm flex items-center gap-1">
                        <XCircle size={12} /> {stats.total_failed} failed
                      </span>
                    ) : (
                      <span className="text-emerald-500 text-sm flex items-center gap-1">
                        <CheckCircle2 size={12} /> All delivered
                      </span>
                    )}
                  </div>
                </motion.div>

                {/* Campaigns Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-slate-500 text-sm font-medium">Campaigns</p>
                    <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                      <BarChart3 size={20} className="text-amber-600" />
                    </div>
                  </div>
                  <p className="font-heading font-extrabold text-3xl text-slate-900 mb-1">
                    {stats?.total_campaigns || 0}
                  </p>
                  <p className="text-slate-400 text-sm">Total created</p>
                </motion.div>
              </div>

              {/* Start Your Campaigns Section - Moved to Left Column */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-slate-50 rounded-[20px] p-6 shadow-md border border-slate-200"
                data-testid="start-campaigns-section"
              >
                <h3 className="font-semibold text-slate-900 mb-5 text-lg">Start Your Campaigns</h3>

                <div className="grid sm:grid-cols-3 gap-4">
                  {/* Step 1 - Connect Email Accounts */}
                  <div className={`p-4 rounded-xl transition-all ${
                    needsAccounts ? "bg-white" : "bg-emerald-50"
                  } shadow-sm`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          needsAccounts ? "bg-slate-100" : "bg-emerald-100"
                        }`}>
                          {needsAccounts ? (
                            <span className="text-slate-600 font-semibold">1</span>
                          ) : (
                            <CheckCircle2 size={18} className="text-emerald-600" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className={`text-sm font-medium ${needsAccounts ? "text-slate-700" : "text-emerald-700"}`}>
                            Connect Email Accounts
                          </span>
                          <span className={`text-xs ${needsAccounts ? "text-slate-400" : "text-emerald-500"}`}>
                            {needsAccounts ? "Add sender accounts" : `${stats?.total_accounts} connected`}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className={`w-full mt-3 rounded-lg text-xs h-9 shadow-sm ${
                        needsAccounts 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }`}
                      onClick={() => navigate("/accounts")}
                      data-testid="quick-add-account-btn"
                    >
                      <Mail size={14} className="mr-1.5" />
                      {needsAccounts ? "Add Accounts" : "Manage"}
                    </Button>
                  </div>

                  {/* Step 2 - Upload Lists */}
                  <div className={`p-4 rounded-xl transition-all ${
                    needsList ? "bg-white" : "bg-emerald-50"
                  } shadow-sm`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          needsList ? "bg-slate-100" : "bg-emerald-100"
                        }`}>
                          {needsList ? (
                            <span className="text-slate-600 font-semibold">2</span>
                          ) : (
                            <CheckCircle2 size={18} className="text-emerald-600" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className={`text-sm font-medium ${needsList ? "text-slate-700" : "text-emerald-700"}`}>
                            Upload Lists
                          </span>
                          <span className={`text-xs ${needsList ? "text-slate-400" : "text-emerald-500"}`}>
                            {needsList ? "Import contacts" : `${stats?.total_lists} list${stats?.total_lists !== 1 ? 's' : ''}`}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className={`w-full mt-3 rounded-lg text-xs h-9 shadow-sm ${
                        needsList 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }`}
                      onClick={() => navigate("/email-lists")}
                      data-testid={needsList ? "quick-upload-btn" : "manage-lists-btn"}
                    >
                      <Users size={14} className="mr-1.5" />
                      {needsList ? "Upload List" : "View Lists"}
                    </Button>
                  </div>

                  {/* Step 3 - Start Campaign (Always visible) */}
                  <div className="p-4 rounded-xl bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-100">
                          <Zap size={16} className="text-amber-600" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-slate-700">Start Campaign</span>
                          <span className="text-xs text-slate-400">Send emails</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg text-xs h-9 shadow-sm"
                      onClick={() => navigate("/campaign")}
                      data-testid="quick-campaign-btn"
                    >
                      <Send size={14} className="mr-1.5" />
                      Create Campaign
                    </Button>
                  </div>
                </div>
              </motion.div>

              {/* How to Launch Your First Campaign - Instruction Steps */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38 }}
                className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100"
                data-testid="instructions-section"
              >
                <h4 className="font-semibold text-slate-800 mb-4 text-base">How to Launch Your First Campaign</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-blue-600 font-semibold text-sm">1</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Connect your email accounts</p>
                      <p className="text-xs text-slate-500 mt-0.5">Add Gmail, Outlook, or SMTP accounts for sending.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-emerald-600 font-semibold text-sm">2</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Upload your contact list</p>
                      <p className="text-xs text-slate-500 mt-0.5">Import your prospects using CSV format.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-amber-600 font-semibold text-sm">3</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Create your campaign</p>
                      <p className="text-xs text-slate-500 mt-0.5">Write your email and choose your recipient list.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-violet-600 font-semibold text-sm">4</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Launch and monitor</p>
                      <p className="text-xs text-slate-500 mt-0.5">Track sends and manage replies from the dashboard.</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Main Graph Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-[20px] p-6 shadow-sm border border-slate-100"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading font-semibold text-lg text-slate-900">
                      Email Activity Overview
                    </h2>
                    <p className="text-slate-400 text-sm">Weekly sending performance</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-slate-500">Sent</span>
                    </div>
                  </div>
                </div>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: '#94a3b8', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: '#94a3b8', fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="sent"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorSent)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Weekly Stats - Moved to Left Column (below Email Activity) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Weekly Stats</h3>
                  <span className="text-xs text-slate-400">Last 7 days</span>
                </div>
                <div className="h-[120px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                      />
                      <Bar 
                        dataKey="sent" 
                        fill="#f43f5e"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total Sent</p>
                      <p className="font-semibold text-slate-800">{stats?.total_sent || 0}</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Current Campaign (if running) */}
              {stats?.current_campaign && (stats.current_campaign.status === "running" || stats.current_campaign.status === "paused") && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-[20px] p-6 shadow-sm border border-slate-100"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        stats.current_campaign.status === "running" 
                          ? "bg-emerald-500 animate-pulse" 
                          : "bg-amber-500"
                      }`} />
                      <h2 className="font-heading font-semibold text-lg text-slate-900">
                        {stats.current_campaign.name || "Active Campaign"}
                      </h2>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        stats.current_campaign.status === "running"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {stats.current_campaign.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {stats.current_campaign.status === "running" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-xl"
                          onClick={() => handlePauseCampaign(stats.current_campaign.campaign_id)}
                        >
                          <Pause size={14} className="mr-1.5" />
                          Pause
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                          onClick={() => handleResumeCampaign(stats.current_campaign.campaign_id)}
                        >
                          <Play size={14} className="mr-1.5" />
                          Resume
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{stats.current_campaign.sent_count}</p>
                      <p className="text-xs text-slate-500">Sent</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{stats.current_campaign.failed_count}</p>
                      <p className="text-xs text-slate-500">Failed</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">
                        {stats.current_campaign.total_emails - stats.current_campaign.sent_count - stats.current_campaign.failed_count}
                      </p>
                      <p className="text-xs text-slate-500">Remaining</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Progress</span>
                      <span className="text-slate-800 font-medium">
                        {Math.round((stats.current_campaign.sent_count / stats.current_campaign.total_emails) * 100)}%
                      </span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(stats.current_campaign.sent_count / stats.current_campaign.total_emails) * 100}%` }}
                        transition={{ duration: 1 }}
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Column (30%) - Plan & Usage, Today's Summary, Account Usage */}
            <div className="space-y-6">
              {/* Plan & Usage Card */}
              {subscriptionData && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 }}
                  className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100"
                  data-testid="plan-usage-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900">Plan & Usage</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-slate-600 text-xs rounded-lg"
                      onClick={() => navigate("/subscription")}
                      data-testid="view-subscription-btn"
                    >
                      Manage
                    </Button>
                  </div>

                  {/* Current Plan Badge */}
                  <div className={`p-3 rounded-xl mb-4 ${
                    subscriptionData.plan_type === "growth" 
                      ? "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200"
                      : subscriptionData.plan_type === "starter"
                      ? "bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-200"
                      : "bg-slate-50 border border-slate-200"
                  }`}>
                    <div className="flex items-center gap-3">
                      {subscriptionData.plan_type === "growth" ? (
                        <Crown size={20} className="text-amber-500" />
                      ) : subscriptionData.plan_type === "starter" ? (
                        <Zap size={20} className="text-blue-500" />
                      ) : (
                        <Shield size={20} className="text-slate-400" />
                      )}
                      <div>
                        <p className="font-semibold text-slate-900 capitalize">
                          {subscriptionData.plan_type} Plan
                        </p>
                        <p className="text-xs text-slate-500">
                          Status: <span className={`font-medium ${
                            subscriptionData.subscription_active ? "text-green-600" : "text-red-500"
                          }`}>
                            {subscriptionData.subscription_status}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Usage Stats */}
                  {subscriptionData.usage && (
                    <div className="space-y-3">
                      {/* Accounts */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">Email Accounts</span>
                          <span className="font-medium text-slate-900">
                            {subscriptionData.usage.accounts.current} / {subscriptionData.usage.accounts.limit}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (subscriptionData.usage.accounts.current / subscriptionData.usage.accounts.limit) * 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Contacts */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">Stored Contacts</span>
                          <span className="font-medium text-slate-900">
                            {subscriptionData.usage.contacts.current.toLocaleString()} / {subscriptionData.usage.contacts.limit.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (subscriptionData.usage.contacts.current / subscriptionData.usage.contacts.limit) * 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Monthly Recipients */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">Monthly Recipients</span>
                          <span className="font-medium text-slate-900">
                            {subscriptionData.usage.recipients.current.toLocaleString()} / {subscriptionData.usage.recipients.limit.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-violet-500 rounded-full transition-all"
                            style={{ width: `${Math.min(100, (subscriptionData.usage.recipients.current / subscriptionData.usage.recipients.limit) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Plan Limits List */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Plan Limits</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Mail size={14} className="text-blue-500" />
                        <span>{subscriptionData.limits?.max_accounts || 3} email accounts</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Users size={14} className="text-emerald-500" />
                        <span>{(subscriptionData.limits?.max_contacts || 500).toLocaleString()} stored contacts</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Send size={14} className="text-violet-500" />
                        <span>{(subscriptionData.limits?.max_monthly_recipients || 500).toLocaleString()} recipients/month</span>
                      </div>
                    </div>
                  </div>

                  {/* Upgrade Button for Free/Starter */}
                  {subscriptionData.plan_type !== "growth" && (
                    <Button
                      className={`w-full mt-4 ${
                        subscriptionData.plan_type === "free"
                          ? "bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600"
                          : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      }`}
                      onClick={() => navigate("/subscription")}
                      data-testid="plan-card-upgrade-btn"
                    >
                      {subscriptionData.plan_type === "free" ? (
                        <>
                          <Zap size={16} className="mr-2" />
                          Upgrade Now
                        </>
                      ) : (
                        <>
                          <Crown size={16} className="mr-2" />
                          Upgrade to Growth
                        </>
                      )}
                    </Button>
                  )}
                </motion.div>
              )}

              {/* Summary Stats Widget */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100"
              >
                <h3 className="font-semibold text-slate-900 mb-4">Today's Summary</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Send size={18} className="text-blue-600" />
                      </div>
                      <span className="text-slate-600 text-sm">Sends Available</span>
                    </div>
                    <span className="font-bold text-xl text-slate-900">{stats?.total_available_today || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-rose-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                        <AlertTriangle size={18} className="text-rose-600" />
                      </div>
                      <span className="text-slate-600 text-sm">Failed Emails</span>
                    </div>
                    <span className="font-bold text-xl text-slate-900">{stats?.total_failed || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <Activity size={18} className="text-emerald-600" />
                      </div>
                      <span className="text-slate-600 text-sm">Lists Uploaded</span>
                    </div>
                    <span className="font-bold text-xl text-slate-900">{stats?.total_lists || 0}</span>
                  </div>
                </div>
              </motion.div>

              {/* Account Usage Widget */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-[20px] p-5 shadow-sm border border-slate-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Account Usage</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-slate-600 text-xs rounded-lg"
                    onClick={() => navigate("/accounts")}
                    data-testid="manage-accounts-btn"
                  >
                    Manage
                  </Button>
                </div>

                {stats?.accounts?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.accounts.slice(0, 4).map((account, index) => {
                      const percentage = Math.round((account.daily_sent / account.daily_limit) * 100);
                      return (
                        <motion.div
                          key={account.account_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 * index }}
                          className="p-3 bg-slate-50 rounded-xl"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${
                                account.status === "connected" ? "bg-emerald-500" : "bg-red-500"
                              }`} />
                              <span className="text-sm font-medium text-slate-700 truncate max-w-[180px]">
                                {account.email}
                              </span>
                            </div>
                            <span className="text-xs font-semibold text-slate-500">
                              {percentage}%
                            </span>
                          </div>
                          <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{ duration: 0.8, delay: index * 0.1 }}
                              className="h-full bg-gradient-to-r from-rose-400 to-rose-500 rounded-full"
                            />
                          </div>
                          <p className="text-xs text-slate-400 mt-1.5">
                            {account.daily_sent} / {account.daily_limit} sent today
                          </p>
                        </motion.div>
                      );
                    })}
                    {stats.accounts.length > 4 && (
                      <p className="text-xs text-slate-400 text-center pt-2">
                        +{stats.accounts.length - 4} more accounts
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Mail size={20} className="text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm mb-3">No accounts connected</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => navigate("/accounts")}
                      data-testid="add-first-account-btn"
                    >
                      Add Account
                    </Button>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
        </div>
      </main>

      {/* Onboarding Tour */}
      <OnboardingTour
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />

      {/* Export All Campaign Data dialog */}
      <ExportReportDialog
        open={exportReportOpen}
        onOpenChange={setExportReportOpen}
        lockType={null}
        title="Export All Campaign Data"
      />
    </div>
  );
}
